import React, {useState} from "react";
import {Formik, Form} from "formik";
import * as Yup from "yup";
import Modal from "react-modal";
import "./proposalStyle.css";
import {geocodeByPlaceId} from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "../shared/SafeGooglePlacesAutocomplete";
import googleMapsApiKey from "../../../config/googleMaps";

// Validation Schema using Yup
const ProposalSchema = Yup.object().shape({
  address: Yup.string()
    .required("veuillez entrer votre adresse complete")
    .min(5, "L'adresse doit contenir au moins 5 caractères"),
  ville: Yup.string()
    .required("veuillez entrer votre adresse complete")
    .min(2, "La ville doit contenir au moins 2 caractères"),
  postCode: Yup.string()
    .required("veuillez entrer votre adresse complete")
    .matches(/^\d{5}$/, "Le code postal doit contenir exactement 5 chiffres"),
  street: Yup.string()
    .nullable()
    .when("address", {
      is: (val) => val && val.length > 0, // Check if address is not empty
      then: (schema) =>
        schema.required(
          "Veuillez entrer votre adresse complète contenant le nom de rue"
        ),

      otherwise: (schema) => schema.notRequired(),
    }),
  streetNumber: Yup.string()
    .nullable()
    .when("address", {
      is: (val) => val && val.length > 0, // Check if address is not empty
      then: (schema) =>
        schema.required(
          "Veuillez entrer votre adresse complète contenant le numéro de la rue"
        ),

      otherwise: (schema) => schema.notRequired(),
    }),
});

// Custom styles for the modal
const modalStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex", // Enables flexbox
    justifyContent: "center", // Centers horizontally
    alignItems: "center", // Centers vertically
  },
  content: {
    position: "relative",

    width: "90%",
    maxWidth: "554px", // Controls modal width
    maxHeight: "80vh", // Ensures it doesn't exceed the viewport height
    margin: "auto", // Centers in case of fallback
    borderRadius: "12px",
    padding: " 48px 20px 20px 20px",
    overflow: "auto", // Enables scroll for overflowing content
  },
};

const AddressModal = ({
  isOpen,
  onClose,
  id,
  initialValues,
  onAccept,
  onAccepted,
}) => {
  const [selectedAddress, setSelectedAddress] = useState(null);

  const handlePlaceSelect = async (address, setFieldValue) => {
    const [place] = await geocodeByPlaceId(address.value.place_id);

    const {long_name: postalCode = ""} =
      place.address_components.find((c) => c.types.includes("postal_code")) ||
      {};
    const {long_name: ville = ""} =
      place.address_components.find((c) => c.types.includes("locality")) || {};
    const street =
      place.address_components.find((c) => c.types.includes("route"))
        ?.long_name || "";
    const streetNumber =
      place.address_components.find((c) => c.types.includes("street_number"))
        ?.long_name || "";

    setFieldValue("streetNumber", streetNumber);
    setFieldValue("street", street);

    setFieldValue("address", address.label);
    setFieldValue("postCode", postalCode);
    setFieldValue("ville", ville);
    setSelectedAddress(address);
  };
  return (
    <Modal
      className={"h-fit bg-white z-999 "}
      isOpen={isOpen}
      onRequestClose={onClose}
      centered
      style={modalStyles}
    >
      <div className="flex flex-col justify-start w-full h-fit">
        <div className="text-[26px] leading-6 text-[#FF6600] w-full text-center font-bold  !mb-8">
          Confirmer votre adresse
        </div>
        <Formik
          initialValues={{
            address: initialValues.address,
            ville: initialValues.ville,
            postCode: initialValues.postCode,
            street: initialValues.address,
            streetNumber: initialValues.address,
          }}
          validationSchema={ProposalSchema}
          onSubmit={(values, {resetForm}) => {
            onAccept(
              {
                id,
                address: values.address,
                city: values.ville,
                zipCode: values.postCode,
              },
              {onAccepted},
            );
            resetForm();
            onClose();
          }}
        >
          {({errors, setFieldValue}) => (
            <Form className="grid grid-cols-2 gap-1 w-full ">
              <div className="form-group w-full col-span-2 ">
                <label className="text-[#3D4465] font-normal leading-3 text-sm">
                  Adresse de l'intervention
                </label>
                <GooglePlacesAutocomplete
                  apiKey={googleMapsApiKey}
                  autocompletionRequest={{
                    componentRestrictions: {country: ["fr"]},
                    types: ["address"],
                  }}
                  selectProps={{
                    value: selectedAddress,
                    defaultInputValue: initialValues.address,
                    onChange: (address) =>
                      handlePlaceSelect(address, setFieldValue),
                    placeholder: "Recherchez une adresse...",
                    styles: {
                      dropdownIndicator: (base) => ({
                        ...base,
                        display: "none", // Hides the dropdown icon
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        display: "none", // Hides the separator line
                      }),
                      control: (base) => ({
                        ...base,
                        borderColor: "#eee", // Matches the other inputs' border color
                        borderWidth: "1px",
                        borderRadius: "0.375rem", // Rounded corners like your other inputs
                        padding: "0.375rem 0.75rem", // Add padding to match the input
                        boxShadow: "none",
                      }),
                      input: (base) => ({
                        ...base,
                        fontSize: "0.875rem", // Matches your other inputs' font size
                        color: "#374151", // Text color
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#9ca3af", // Placeholder color
                        fontSize: "0.875rem",
                      }),
                    },
                  }}
                />
                {(errors.address ||
                  errors.postCode ||
                  errors.ville ||
                  errors.street ||
                  errors.streetNumber) && (
                  <div
                    className="invalid-feedback animated fadeInUp"
                    style={{display: "block"}}
                  >
                    {errors.address ||
                      errors.postCode ||
                      errors.ville ||
                      errors.street ||
                      errors.streetNumber}
                  </div>
                )}
              </div>
              <div className=" flex flex-col gap-2 col-span-2 border bg-[#f7f7f7] !rounded-lg !px-7 !py-3 max-h-[250px] overflow-y-scroll  ">
                <div className="flex flex-col gap-2 ">
                  <div className="text-[#3D4465] font-normal text-sm">
                    {" "}
                    Titre de l'intervention
                  </div>
                  <div className="text-[#19295C] font-semibold text-sm">
                    {initialValues.title}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ">
                  <div className="text-[#3D4465] font-normal text-sm">
                    {" "}
                    Date de RDV
                  </div>
                  <div className="text-[#19295C] font-semibold text-sm">
                    {initialValues.appointmentDate}
                  </div>
                </div>

                <div className="flex flex-col gap-2 ">
                  <div className="text-[#3D4465] font-normal text-sm">
                    {" "}
                    Prix proposé
                  </div>
                  <div className="text-[#19295C] font-semibold text-sm">
                    {initialValues.initialPrice}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ">
                  <div className="text-[#3D4465] font-normal text-sm">
                    {" "}
                    Commentaire
                  </div>
                  <div className="text-[#19295C] font-semibold text-sm">
                    {!!initialValues?.comment ? initialValues?.comment : "--"}
                  </div>
                </div>
                <div className="text-[#3D4465] font-light text-[10px] mt-2">
                  NB : pour une annulation par le client, le remboursement est
                  de 100 % au moins 6 heures avant le rendez-vous, 70 % entre
                  4 heures et moins de 6 heures, 50 % entre 2 heures et moins de
                  4 heures, et 0 % moins de 2 heures avant le rendez-vous.
                </div>
              </div>

              <div className="flex col-span-2 justify-center w-full !mt-4 gap-4">
                <button
                  onClick={onClose}
                  className="w-1/2   text-sm flex justify-center items-center !py-3 px-2 min-w-[100px]  !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border  border-solid"
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="w-1/2 text-sm flex justify-center items-center py-3 px-2 min-w-[100px]  !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white "
                >
                  Accepter la proposition
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default AddressModal;
